import { Button, Stack, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ModalV2 from '/@/components/bayer/modal-v2';
import settings from '/@/core/settings';
import { useAssignFieldsToGrowerMutation, useUpdateFieldMutation } from '/@/hooks/fields';
import { type UserNode, useGetUserNode, useOwnUserNode } from '/@/hooks/nodes';
import { _ } from '/@/locale';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { Field } from '/@/types/field';
import { reverse } from '/@/utils/router';
import FieldSelectorModal from '/@/views/dashboard/growers/components/field-selector-modal';
import GrowerSelectorModal from '/@/views/dashboard/growers/components/grower-selector-modal';

const SuccessAssignFieldModal = ({
    open,
    setOpen,
    field,
    selectedUserNode,
}: {
    open: boolean;
    setOpen: (open: boolean) => void;
    field?: Field;
    selectedUserNode: UserNode;
}) => {
    const navigate = useNavigate();
    return (
        <ModalV2
            title={field ? _('¡Lote asignado con éxito!') : _('¡Lotes asignados con éxito!')}
            open={open}
            onClose={() => {
                setOpen(false);
            }}
            actions={
                <Stack direction='row' spacing={2} justifyContent='end' width='100%'>
                    <Button
                        variant='contained'
                        color='secondary'
                        onClick={() => {
                            navigate(
                                reverse('Growers.detail', {
                                    params: { growerNodeId: field?.userNode ?? selectedUserNode.id },
                                })
                            );
                        }}
                    >
                        {_('Ir al detalle del productor')}
                    </Button>
                </Stack>
            }
        >
            <Typography variant='body1' color='primary'>
                {field
                    ? _('El lote {{field}} ahora está vinculado a {{grower}} en {{app}}.', {
                          field: field.name,
                          grower: selectedUserNode?.name,
                          app: settings.app.name,
                      })
                    : _('Los lotes ahora están vinculados a {{grower}} en {{app}}.', {
                          grower: selectedUserNode?.name,
                          app: settings.app.name,
                      })}
            </Typography>
        </ModalV2>
    );
};

const AssignFieldModal = ({
    open,
    setOpen,
    field,
}: {
    open: boolean;
    setOpen: (open: boolean) => void;
    field?: Field;
}) => {
    const ownUserNode = useOwnUserNode();
    const [selectedUserNodeId, setSelectedUserNodeId] = useState<number | null>(null);
    const selectedUserNode = useGetUserNode(selectedUserNodeId ?? 0);
    const [step, setStep] = useState<'assign-field-to-grower' | 'success' | 'select-fields'>('assign-field-to-grower');

    const assignFieldToGrowerMutation = useUpdateFieldMutation(field?.id ?? 0);
    const assignFieldsToGrowerMutation = useAssignFieldsToGrowerMutation();

    const handleAssignFieldToGrower = async () => {
        if (!field || selectedUserNodeId == null) return;

        try {
            await assignFieldToGrowerMutation.trigger({
                userNodeId: selectedUserNodeId,
            });
            mpTrackEvent('Field Assigned to Grower', {
                field_id: field.id,
                grower_id: selectedUserNodeId,
            });
            setStep('success');
        } catch {
            enqueueSnackbar(_('Error al asignar el lote'), { variant: 'error' });
        }
    };

    const handleAssignFieldsToGrower = async (fieldIds: number[]) => {
        if (!fieldIds.length || selectedUserNodeId == null) return;

        try {
            await assignFieldsToGrowerMutation.trigger({
                fieldIds,
                userNodeId: selectedUserNodeId,
            });
            fieldIds.forEach((fieldId) => {
                mpTrackEvent('Field Assigned to Grower', {
                    field_id: fieldId,
                    grower_id: selectedUserNodeId,
                });
            });
            setStep('success');
        } catch {
            enqueueSnackbar(_('Error al asignar los lotes'), { variant: 'error' });
        }
    };

    if (!ownUserNode.data?.id) return null;
    return (
        <>
            <GrowerSelectorModal
                userNodeId={ownUserNode.data?.id}
                ModalV2Props={{
                    open: open && step === 'assign-field-to-grower',
                    title: _('Elegí el productor para este lote'),
                    onClose: () => {
                        setOpen(false);
                    },
                    bodyProps: {
                        sx: {
                            overflow: 'auto',
                            maxHeight: { xs: '80vh' },
                            alignItems: 'flex-start',
                            textAlign: 'left',
                        },
                        spacing: 3,
                    },
                    actionsProps: {
                        sx: {
                            justifyContent: 'end',
                        },
                    },
                    maxWidth: 'md',
                }}
                selectedUserNodeId={selectedUserNodeId}
                setSelectedUserNodeId={setSelectedUserNodeId}
                text={
                    field
                        ? _(`Seleccioná un productor para asignar el lote ${field.name}.`)
                        : _('Seleccioná un productor para asignarle lotes.')
                }
                nextAction={
                    field
                        ? {
                              onClick: handleAssignFieldToGrower,
                              text: _('Asignar lote'),
                          }
                        : {
                              onClick: () => setStep('select-fields'),
                              text: _('Continuar'),
                          }
                }
            />

            <FieldSelectorModal
                ModalV2Props={{
                    open: open && step === 'select-fields',
                    onClose: () => setOpen(false),
                    title: _('Elegí los lotes que queres asignar'),
                }}
                text={_('Seleccioná los lotes que queres asignarle a {{growerName}}.', {
                    growerName: selectedUserNode?.data?.name,
                })}
                userNodeId={ownUserNode.data?.id}
                action={{
                    onClick: handleAssignFieldsToGrower,
                    text: _('Terminar'),
                }}
                field={field}
            />
            {selectedUserNode?.data && (
                <SuccessAssignFieldModal
                    open={open && step === 'success'}
                    setOpen={setOpen}
                    field={field}
                    selectedUserNode={selectedUserNode.data}
                />
            )}
        </>
    );
};

export default AssignFieldModal;
